import type { DragEndEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { Switch } from '@heroui/react'
import { useParams } from 'react-router'
import type { RadarConfig } from '../../../../types'

import { SortableItem } from './SortableItem'

interface DisplaySettingsProps {
  config: RadarConfig | null
  setConfig: (config: RadarConfig) => void
}

export const DisplaySettings = ({
  config,
  setConfig
}: DisplaySettingsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const { radarId } = useParams()

  const handleToggleChange = async (
    toggleName: keyof RadarConfig['toggles'],
    checked: boolean
  ) => {
    if (!config) return
    const updatedConfig = {
      ...config,
      toggles: {
        ...config.toggles,
        [toggleName]: checked
      }
    }
    setConfig(updatedConfig)
  }

  // Handler for section reordering
  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !config) return

    if (active.id !== over.id) {
      const sections = [...config.sections] // Create mutable copy
      const oldIndex = sections.indexOf(active.id as string)
      const newIndex = sections.indexOf(over.id as string)

      // Create new array and update sections directly
      const newSections = arrayMove(sections, oldIndex, newIndex)
      const updatedConfig = { ...config, sections: newSections }
      setConfig(updatedConfig)
    }
  }

  // Handler for quadrant reordering
  const handleQuadrantDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || !config) return

    if (active.id !== over.id) {
      const quadrants = [...config.quadrants] // Create mutable copy
      const oldIndex = quadrants.findIndex(q => q.id === active.id)
      const newIndex = quadrants.findIndex(q => q.id === over.id)

      // Create new array and update quadrants directly
      const newQuadrants = arrayMove(quadrants, oldIndex, newIndex)

      // Update positions based on new order
      const updatedQuadrants = newQuadrants.map((quadrant, index) => ({
        ...quadrant,
        position: index + 1
      }))

      const updatedConfig = { ...config, quadrants: updatedQuadrants }
      setConfig(updatedConfig)
    }
  }

  // Early return if no config yet
  if (!config) return null

  return (
    <div className="space-y-6" data-testid="display-settings">
      <div className="border-b border-[var(--ds-border)] pb-6">
        <h3
          className="text-lg font-medium text-[var(--ds-text)] mb-4"
          data-testid="display-settings-title"
        >
          Display Settings
        </h3>
        <div className="space-y-6">
          <div>
            <h4
              className="text-sm font-medium text-[var(--ds-text)] mb-2"
              data-testid="feature-toggles-title"
            >
              Feature Toggles
            </h4>
            <div
              className="divide-y divide-[var(--ds-border)]"
              data-testid="feature-toggles-list"
            >
              <div
                className="flex items-center justify-between p-3"
                data-testid="show-chart-toggle"
              >
                <div>
                  <div
                    className="text-sm text-[var(--ds-text)]"
                    id="show-chart-label"
                  >
                    Show Chart
                  </div>
                  <div
                    className="text-xs text-[var(--ds-text-subtle)]"
                    id="show-chart-description"
                  >
                    Display the radar chart visualization
                  </div>
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    minWidth: 48
                  }}
                >
                  <Switch
                    aria-label="Show Chart"
                    aria-describedby="show-chart-description"
                    isSelected={config?.toggles?.showChart ?? true}
                    onValueChange={checked =>
                      handleToggleChange('showChart', checked)
                    }
                    data-testid="show-chart-switch"
                  />
                </div>
              </div>

              <div
                className="flex items-center justify-between p-3"
                data-testid="show-tag-filter-toggle"
              >
                <div>
                  <div
                    className="text-sm text-[var(--ds-text)]"
                    id="show-tag-filter-label"
                  >
                    Show Tag Filter
                  </div>
                  <div
                    className="text-xs text-[var(--ds-text-subtle)]"
                    id="show-tag-filter-description"
                  >
                    Display the tag filter for items
                  </div>
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    minWidth: 48
                  }}
                >
                  <Switch
                    aria-label="Show Tag Filter"
                    aria-describedby="show-tag-filter-description"
                    isSelected={config?.toggles?.showTagFilter ?? true}
                    onValueChange={checked =>
                      handleToggleChange('showTagFilter', checked)
                    }
                    data-testid="show-tag-filter-switch"
                  />
                </div>
              </div>

              <div
                className="flex items-center justify-between p-3"
                data-testid="show-quadrant-list-toggle"
              >
                <div>
                  <div
                    className="text-sm text-[var(--ds-text)]"
                    id="show-quadrant-list-label"
                  >
                    Show Quadrant List
                  </div>
                  <div
                    className="text-xs text-[var(--ds-text-subtle)]"
                    id="show-quadrant-list-description"
                  >
                    Display the quadrant list below the chart
                  </div>
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    minWidth: 48
                  }}
                >
                  <Switch
                    aria-label="Show Quadrant List"
                    aria-describedby="show-quadrant-list-description"
                    isSelected={config?.toggles?.showQuadrantList ?? true}
                    onValueChange={checked =>
                      handleToggleChange('showQuadrantList', checked)
                    }
                    data-testid="show-quadrant-list-switch"
                  />
                </div>
              </div>

              <div
                className="flex items-center justify-between p-3"
                data-testid="show-empty-rings-toggle"
              >
                <div>
                  <div
                    className="text-sm text-[var(--ds-text)]"
                    id="show-empty-rings-label"
                  >
                    Show Empty Rings
                  </div>
                  <div
                    className="text-xs text-[var(--ds-text-subtle)]"
                    id="show-empty-rings-description"
                  >
                    Display empty rings in the radar chart
                  </div>
                </div>
                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    minWidth: 48
                  }}
                >
                  <Switch
                    aria-label="Show Empty Rings"
                    aria-describedby="show-empty-rings-description"
                    isSelected={config?.toggles?.showEmptyRings ?? false}
                    onValueChange={checked =>
                      handleToggleChange('showEmptyRings', checked)
                    }
                    data-testid="show-empty-rings-switch"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h4
              className="text-sm font-medium text-[var(--ds-text)] mb-2"
              data-testid="section-order-title"
            >
              Section Order
            </h4>
            <section
              className="p-3 border border-[var(--ds-border)] rounded-[3px]"
              style={{
                position: 'relative',
                minHeight: '100px'
              }}
              data-testid="section-order-container"
              aria-labelledby="section-order-title"
            >
              <div
                className="text-xs text-[var(--ds-text-subtle)] mb-3"
                data-testid="section-order-instructions"
                role="note"
              >
                Drag and drop sections to reorder them
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleSectionDragEnd}
              >
                <SortableContext
                  items={[...config.sections]}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2" data-testid="section-order-list">
                    {[...config.sections].map(section => (
                      <SortableItem key={section} id={section}>
                        <div
                          className="text-[var(--ds-text-subtle)]"
                          aria-hidden="true"
                        >
                          ⋮⋮
                        </div>
                        <div
                          className="text-sm text-[var(--ds-text)] capitalize"
                          data-testid={`section-item-${section}`}
                        >
                          {section}
                        </div>
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          </div>

          <div>
            <h4
              className="text-sm font-medium text-[var(--ds-text)] mb-2"
              data-testid="quadrant-order-title"
            >
              Quadrant Order
            </h4>
            <section
              className="p-3 border border-[var(--ds-border)] rounded-[3px]"
              style={{
                position: 'relative',
                minHeight: '100px'
              }}
              data-testid="quadrant-order-container"
              aria-labelledby="quadrant-order-title"
            >
              <div
                className="text-xs text-[var(--ds-text-subtle)] mb-3"
                data-testid="quadrant-order-instructions"
                role="note"
              >
                Drag and drop quadrants to reorder them
              </div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleQuadrantDragEnd}
              >
                <SortableContext
                  items={[...config.quadrants].map(q => q.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2" data-testid="quadrant-order-list">
                    {[...config.quadrants].map(quadrant => (
                      <SortableItem key={quadrant.id} id={quadrant.id}>
                        <div
                          className="text-[var(--ds-text-subtle)]"
                          aria-hidden="true"
                        >
                          ⋮⋮
                        </div>
                        <div
                          className="w-4 h-4 rounded-xs"
                          style={{ backgroundColor: quadrant.color }}
                          aria-hidden="true"
                          data-testid={`quadrant-color-${quadrant.id}`}
                        />
                        <div
                          className="text-sm text-[var(--ds-text)]"
                          data-testid={`quadrant-item-${quadrant.id}`}
                        >
                          {quadrant.title}
                        </div>
                      </SortableItem>
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
